import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingShell.css';
import { ACCESS_MAILTO, TALK_MAILTO } from '../utils/contactMailto';

// Landing-style right arrow (matches the hero CTA glyph).
export const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/**
 * Landing-styled button. Mirrors the landing's .btn system so CTAs across
 * login/legal match the home page pixel-for-pixel.
 * kind: 'grad' | 'ghost' | 'glass'
 */
export const LBtn = ({ kind = 'grad', size, block, iconRight, className = '', children, ...props }) => (
  <button
    className={[
      'lbtn', `lbtn--${kind}`,
      size ? `lbtn--${size}` : '',
      block ? 'lbtn--block' : '',
      className,
    ].filter(Boolean).join(' ')}
    {...props}
  >
    {children}
    {iconRight && <ArrowRight />}
  </button>
);

const FootCol = ({ title, children }) => (
  <div className="lshell-foot-col">
    <h4>{title}</h4>
    {children}
  </div>
);

// The landing's wordmark: a soft violet-to-orchid ramp across the word.
// Same shape in the nav and the footer, as on the landing itself.
const Brand = ({ onClick }) => (
  <button type="button" className="lshell-brand" onClick={onClick}>
    <b>Qombra</b>
  </button>
);

/**
 * LandingShell — the shared chrome (noise aurora + frosted-on-scroll nav +
 * 4-column footer) that makes login/imprint/privacy match the landing.
 * Always light, independent of the app's global theme.
 *
 * @param navRight   nodes rendered on the right of the nav (page CTA)
 * @param children   the page body
 */
const LandingShell = ({ navRight, children }) => {
  const navigate = useNavigate();
  const hdrRef = useRef(null);
  const [year] = useState(() => new Date().getFullYear());

  // One CTA, mirroring the landing header. Self-serve sign-up is closed: there
  // is no public /signup, so offering "Create account" here would have walked
  // the reader into a redirect back to this same button.
  // Pass navRight (incl. null) to override — auth pages do this.
  const right = navRight !== undefined ? navRight : (
    <LBtn kind="grad" size="sm" onClick={() => navigate('/login')}>Log in</LBtn>
  );

  // Frost the nav once the page scrolls, exactly like the landing header.
  useEffect(() => {
    const hdr = hdrRef.current;
    if (!hdr) return undefined;
    const onScroll = () => hdr.classList.toggle('scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goHome = () => navigate('/');

  return (
    <div className="lshell">
      <div className="lshell-aurora" aria-hidden="true" />

      <header className="lshell-hdr" ref={hdrRef}>
        <div className="lshell-wrap">
          <nav className="lshell-nav">
            <Brand onClick={goHome} />
            <div className="lshell-nav-right">{right}</div>
          </nav>
        </div>
      </header>

      <main className="lshell-main">{children}</main>

      <footer className="lshell-footer">
        <div className="lshell-wrap">
          <div className="lshell-foot-grid">
            <div className="lshell-foot-brand">
              <Brand onClick={goHome} />
              <p>Predictive intelligence for optimal decisions.</p>
            </div>
            {/* These are the landing's own section ids — LandingPage.jsx scrolls
                to an incoming hash on mount, so they land in the right place
                from here. #demo / #usecases went away with the old design. */}
            <FootCol title="Research">
              <a href="/#qbrain" onClick={(e) => { e.preventDefault(); navigate('/#qbrain'); }}>QBrain</a>
              <a href="/#qagent" onClick={(e) => { e.preventDefault(); navigate('/#qagent'); }}>QAgent</a>
              <a href="/#pipeline" onClick={(e) => { e.preventDefault(); navigate('/#pipeline'); }}>Pipeline</a>
              <a href="/#integrations" onClick={(e) => { e.preventDefault(); navigate('/#integrations'); }}>Integrations</a>
              {/* Django-served page, not a SPA route — plain navigation. */}
              <a href="/api/docs/">SDK Docs</a>
            </FootCol>
            <FootCol title="Company">
              {/* Static generated page, not a SPA route — plain navigation. */}
              <a href="/blog/">Blog</a>
              <a href={TALK_MAILTO}>Contact</a>
              <a href={ACCESS_MAILTO}>Get access</a>
              <a href="https://www.linkedin.com/in/jan-henrik-bertrand-01a2871a0/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </FootCol>
            <FootCol title="Legal">
              <a href="/imprint" onClick={(e) => { e.preventDefault(); navigate('/imprint'); }}>Imprint</a>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Privacy</a>
              <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>Terms</a>
              <a href="/aup" onClick={(e) => { e.preventDefault(); navigate('/aup'); }}>Acceptable use</a>
              <a href="/dpa" onClick={(e) => { e.preventDefault(); navigate('/dpa'); }}>DPA</a>
            </FootCol>
          </div>
          <div className="lshell-foot-bottom">
            <span className="meta">© {year} Qombra</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingShell;
